import { ExportStatus } from "@tc/ui-dfe/query"
import { defaultDateFormat, defaultDateTimeFormat } from "../constants"
import dayJs, { Dayjs } from "../libs/dayjs"

const capitalize = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()

type DateInput = string | number | Dayjs

export const toDayJs = (date?: DateInput, sourceFormatter?: string) => {
  if (!date) return null
  if (typeof date === "number") {
    return dayJs(date)
  }
  if (typeof date === "string") {
    return dayJs(date, sourceFormatter)
  }
  return date
}

export const toDate = (
  date?: DateInput,
  formatter = defaultDateFormat,
  sourceFormatter?: string,
) => {
  const _date = toDayJs(date, sourceFormatter)
  if (!_date) return null
  if (_date.isValid()) {
    return _date.format(formatter)
  } else {
    return "-"
  }
}

export const toPacificAucklandDateFormat = (
  date?: DateInput,
  formatter = defaultDateFormat,
) => {
  if (!date) return null
  let _date
  if (typeof date === "number" || typeof date === "string") {
    _date = dayJs(date)
  } else {
    _date = date
  }
  if (_date?.isValid()) {
    return _date.tz("Pacific/Auckland").format(formatter)
  }
  return "-"
}

export const toDateTime = (
  date?: DateInput,
  formatter = defaultDateTimeFormat,
  sourceFormatter?: string,
) => {
  const _date = toDayJs(date, sourceFormatter)
  if (!_date) return null
  if (_date.isValid()) {
    return _date.format(formatter)
  } else {
    return "-"
  }
}

export const toISOtime = (date?: DateInput) => {
  const _date = toDayJs(date)
  if (!_date) return null
  if (_date.isValid()) {
    return _date.toISOString()
  } else {
    return null
  }
}
// products.0.process.6.type
export const normalizeFieldPath = (path: string): string => {
  return path.replaceAll(/\[(\d+)\]/g, ".$1")
}

// products[*].process[*].type
export const generateServicePath = (path: string): string => {
  return path.replaceAll(/\[\d+\]/g, "[*]")
}

export const toLabelValue = <T extends Partial<Record<keyof T, string>>>(
  records: T,
) => {
  return Object.entries(records).map(([key, value]) => {
    return { value: key, label: value as string }
  })
}

export const arrayToLabelValue = (list: Array<number | string>) => {
  return list.map((value) => {
    return { value, label: value.toString() }
  })
}

const NumberFormat = (value: number, digits: number) => {
  return new Intl.NumberFormat("en-NZ", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
}
export const ParseNumber = (value: number | string) =>
  typeof value === "number"
    ? isNaN(value)
      ? 0
      : value
    : (value && Number(value.replace(/,/g, ""))) || 0

export const ToNumber = (value: number, digits = 2, isRound = false) => {
  const num = ParseNumber(value)
  const adj = Math.pow(10, digits)
  const floor = Math.floor(num * adj) / adj
  const round = Math.round(num * adj) / adj
  let decimal = digits
  let formatted = NumberFormat(isRound ? round : floor, decimal)
  if (value > 0 && Number(formatted) === 0) {
    decimal += 1
    formatted = ToNumber(value, decimal, isRound)
  }
  return formatted
}

// Status label mapping - matches backend status-label-mapper.ts
const statusLabelMap: Record<string, string> = {
  AMENDED: "Amended",
  APPROVED: "Approved",
  CANCELLED: "Cancelled",
  DRAFT: "Draft",
  RAISED: "Raised",
  REPLACED: "Replaced",
  REQUEST_AMENDMENT: "Request Amendment",
  RESUBMIT: "Resubmit",
  REVOKED: "Revoked",
  TO_BE_AMENDED: "To be Amended",
  PENDING_REPLACEMENT: "Pending Replacement",
  ACKNOWLEDGED: "Acknowledged",
  ACCEPTED: "Accepted",
  DETAINED: "Detained",
  REJECTED: "Rejected",
  PARTIAL: "Partial",
  REQUEST_REPLACEMENT: "Replacement Requested",
}

export const toCheckStatus = (status: string, substatus?: string | null) => {
  if (!status) return ""

  const statusLabel =
    statusLabelMap[status] ?? capitalize(status.replaceAll("_", " "))

  // substatus render for Raised and Amended ERs
  if (
    [ExportStatus.Raised, ExportStatus.Amended].includes(
      status as ExportStatus,
    ) &&
    substatus
  ) {
    return statusLabel + " - " + capitalize(substatus.replaceAll("_", " "))
  }

  return statusLabel
}

export const toCapitalize = (str = "") => {
  if (!str) return ""
  return capitalize(str.replaceAll("_", " "))
}

export const isInEnum = <T extends object>(value: string, enumObject: T) => {
  return Object.values(enumObject).includes(value)
}

export const stringToLabelValue = (value: string[]) => {
  return toLabelValue(Object.fromEntries(value.map((v) => [v, v])))
}

export const toErrorMessage = (error: string) => {
  return error.replace("[GraphQL] ", "")
}

export const toPageAttributes = (
  pathname: string,
  params: {
    [key: string]: string | string[]
  },
) => {
  if (!params.id || !Array.isArray(params.id)) {
    return pathname
  }
  return pathname.replace(params.id.join("/"), ":id")
}

export const toCamelCase = (input: string) => {
  return input
    .toLowerCase()
    .split(" ")
    .map((word: string, index: number) => {
      if (index === 0) return word
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join("")
}
